import React, { PureComponent } from "react";
import { RouteComponentProps } from "react-router";
import DefaultLayout from "layouts/Default";
import "./index.scss";
import { BlogPost, ReportRequest } from "services/entities/content";
import { loadBlogPost, requestBlogReport } from "services/content";
import { handleError, NotFoundError } from "util/errors";
import RouteURLs from "RouteURLs";
import Form, { FormStatus } from "components/forms/Form";
import { ValidationGroup, Validators } from "util/validation";
import TextField from "components/forms/TextField";
import { Button } from "@material-ui/core";
import ProgressSpinner from "components/ProgressSpinner";
import PhoneNumberField from "components/forms/PhoneNumberField";
import BlogPostCard from "pages/blog/components/BlogPostCard";

interface ViewReportPageState {
  status: FormStatus;
  blogPost: BlogPost | null;
  reportURL: URL | null;
}

export default class ViewReportPage extends PureComponent<
  RouteComponentProps<{ code: string }>,
  ViewReportPageState
> {
  private readonly validation: ValidationGroup;
  private readonly model: ReportRequest;

  state = {
    status: FormStatus.None,
    blogPost: null,
    reportURL: null,
  };

  constructor(props: any) {
    super(props);
    this.model = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
    };
    this.validation = new ValidationGroup(this.model);
    this.validation.addField("firstName", { required: Validators.required });
    this.validation.addField("lastName", {});
    this.validation.addField("phone", { phone: Validators.phone });
    this.validation.addField("company", { required: Validators.required });
    this.validation.addField("email", {
      required: Validators.required,
      email: Validators.email,
    });
  }

  componentDidMount(): void {
    loadBlogPost(this.props.match.params.code).then(
      (post) => this.setState({ blogPost: post }),
      (err) => {
        if (err instanceof NotFoundError) {
          this.props.history.push(RouteURLs.NOT_FOUND);
        } else {
          handleError(err);
        }
      }
    );
  }

  onSubmit = () => {
    this.setState({ status: FormStatus.Submitting });
    requestBlogReport(this.props.match.params.code, this.model).then(
      (url) => {
        this.setState({ status: FormStatus.Success, reportURL: url });
      },
      (ex: any) => {
        this.setState({ status: FormStatus.Failure });
        handleError("Failed requesting report", ex);
      }
    );
  };

  onFieldInput = () => {
    this.setState({ status: FormStatus.None });
  };

  renderBlogPost = () => {
    const post: BlogPost | null = this.state.blogPost;
    if (!post) {
      return (
        <section className="blog-post">
          <ProgressSpinner />
        </section>
      );
    } else {
      return <BlogPostCard blogPost={post} displayActions={false} />;
    }
  };

  renderRequestReportForm = () => {
    const disabled =
      this.state.status === FormStatus.Submitting ||
      this.state.status === FormStatus.Success;
    return (
      <Form onSubmit={this.onSubmit} validationGroup={this.validation}>
        <h2>Fill in the form below to download now</h2>
        <TextField
          name="firstName"
          label="First name"
          className="first-name"
          type="text"
          required={true}
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{ required: "First name is required" }}
          disabled={disabled}
          onInput={this.onFieldInput}
        />
        <TextField
          name="lastName"
          label="Last name"
          className="last-name"
          type="text"
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{ required: "First name is required" }}
          disabled={disabled}
          onInput={this.onFieldInput}
        />
        <TextField
          name="email"
          label="Work Email"
          className="email"
          type="text"
          required={true}
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{
            required: "Email is required",
            email: "Invalid email",
          }}
          disabled={disabled}
          onInput={this.onFieldInput}
        />
        <PhoneNumberField
          name="phone"
          label="Phone"
          className="phone"
          validationGroup={this.validation}
          model={this.model}
          validationMessages={{
            required: "Email is required",
            phone: "Invalid phone number",
          }}
          disabled={disabled}
          onInput={this.onFieldInput}
        />
        <TextField
          name="company"
          label="Company"
          className="company"
          type="text"
          required={true}
          validationGroup={this.validation}
          validationMessages={{ required: "Company is required" }}
          model={this.model}
          disabled={disabled}
          onInput={this.onFieldInput}
        />
        <section className="actions">
          <Button className="submit" type="submit" disabled={disabled}>
            {this.state.status === FormStatus.Submitting && <ProgressSpinner />}
            Submit
          </Button>
        </section>
      </Form>
    );
  };

  renderSuccessMessage() {
    const url: URL | null = this.state.reportURL;
    return (
      <section className="success">
        <h2>Your information has been submitted</h2>
        <a
          href={url!.toString()}
          download={true}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download report
        </a>
      </section>
    );
  }

  render() {
    const status = this.state.status;
    return (
      <DefaultLayout id="view-report-page">
        {this.renderBlogPost()}
        {status !== FormStatus.Success && this.renderRequestReportForm()}
        {status === FormStatus.Success && this.renderSuccessMessage()}
      </DefaultLayout>
    );
  }
}
